<template>
  <span
    class="iban-masked-input"
    :class="{
      'iban-masked-input--invalid': invalid,
      'iban-masked-input--disabled': disabled,
    }"
  >
    <span v-if="showMask" class="iban-masked-input__mask" aria-hidden="true">
      {{ maskText }}
    </span>
    <input
      ref="input"
      class="iban-masked-input__control"
      :value="displayValue"
      :placeholder="showMask ? '' : placeholder"
      :aria-label="ariaLabel"
      :aria-invalid="invalid ? 'true' : 'false'"
      :disabled="disabled"
      maxlength="31"
      autocomplete="off"
      autocapitalize="characters"
      spellcheck="false"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown="handleKeydown"
      @input="handleInput"
    />
  </span>
</template>

<script>
import { formatItalianIban, normalizeIban } from "@/utils/f24Banking";

const IBAN_TEMPLATE = "IT00-X-00000-00000-000000000000";

function ibanCharacterCount(value, position = String(value || "").length) {
  return normalizeIban(String(value || "").slice(0, position)).length;
}

function caretPositionForCharacterCount(value, characterCount) {
  if (characterCount <= 0) return 0;

  let seen = 0;
  for (let index = 0; index < value.length; index += 1) {
    if (/[A-Z0-9]/i.test(value[index])) seen += 1;
    if (seen === characterCount) {
      let position = index + 1;
      while (value[position] === "-") position += 1;
      return position;
    }
  }
  return value.length;
}

export default {
  name: "IbanMaskedInput",
  props: {
    value: { type: String, default: "" },
    invalid: Boolean,
    disabled: Boolean,
    ariaLabel: { type: String, default: "IBAN facoltativo" },
    placeholder: { type: String, default: "Facoltativo" },
  },
  data() {
    return {
      focused: false,
      displayValue: formatItalianIban(this.value),
    };
  },
  computed: {
    normalizedValue() {
      return normalizeIban(this.value).slice(0, 27);
    },
    showMask() {
      return this.focused || Boolean(this.normalizedValue);
    },
    maskText() {
      const visibleLength = this.displayValue.length;
      return `${"\u00a0".repeat(visibleLength)}${IBAN_TEMPLATE.slice(
        visibleLength
      )}`;
    },
  },
  watch: {
    value(value) {
      const formatted = formatItalianIban(value);
      if (formatted !== this.displayValue) this.displayValue = formatted;
    },
  },
  methods: {
    applyValue(value, characterIndex, input = this.$refs.input) {
      const normalized = normalizeIban(value).slice(0, 27);
      const formatted = formatItalianIban(normalized);
      this.displayValue = formatted;
      if (input) input.value = formatted;
      this.$emit("input", normalized);

      this.$nextTick(() => {
        if (!input || document.activeElement !== input) return;
        const position = caretPositionForCharacterCount(
          formatted,
          Math.min(characterIndex, normalized.length)
        );
        input.setSelectionRange?.(position, position);
      });
    },
    handleFocus(event) {
      this.focused = true;
      this.$emit("focus", event);
    },
    handleBlur(event) {
      this.focused = false;
      this.$emit("blur", event);
    },
    handleInput(event) {
      const input = event.currentTarget;
      const characterIndex = ibanCharacterCount(
        input.value,
        input.selectionStart ?? input.value.length
      );
      this.applyValue(input.value, characterIndex, input);
    },
    handleKeydown(event) {
      if (event.key !== "Backspace" && event.key !== "Delete") return;

      const input = event.currentTarget;
      const formatted = input.value;
      const normalized = normalizeIban(formatted).slice(0, 27);
      const selectionStart = input.selectionStart ?? 0;
      const selectionEnd = input.selectionEnd ?? selectionStart;
      let startIndex = ibanCharacterCount(formatted, selectionStart);
      let endIndex = ibanCharacterCount(formatted, selectionEnd);

      event.preventDefault();

      if (startIndex === endIndex) {
        if (event.key === "Backspace") {
          if (startIndex === 0) return;
          startIndex -= 1;
        } else {
          if (startIndex >= normalized.length) return;
          endIndex += 1;
        }
      }

      const nextValue = `${normalized.slice(0, startIndex)}${normalized.slice(
        endIndex
      )}`;
      this.applyValue(nextValue, startIndex, input);
    },
  },
};
</script>

<style scoped>
.iban-masked-input {
  position: relative;
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: var(--iban-input-height, 2rem);
  overflow: hidden;
  border: 1px solid #cbd6df;
  border-radius: 6px;
  background: #fff;
  color: #233b50;
  font-family: Consolas, "Courier New", monospace;
  font-size: inherit;
  letter-spacing: 0.01em;
  line-height: 1;
}

.iban-masked-input:focus-within {
  border-color: #7aa7d9;
  box-shadow: 0 0 0 2px rgba(35, 103, 173, 0.12);
}

.iban-masked-input--invalid:not(:focus-within) {
  border-color: #dc4c4c;
  background: #fff8f8;
}

.iban-masked-input--disabled {
  background: #f3f5f7;
  color: #7b8792;
}

.iban-masked-input__mask,
.iban-masked-input__control {
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 0 0.45rem;
  border: 0;
  font: inherit;
  letter-spacing: inherit;
  line-height: var(--iban-input-height, 2rem);
  text-transform: uppercase;
  white-space: pre;
}

.iban-masked-input__mask {
  z-index: 1;
  overflow: hidden;
  color: #aab4bd;
  pointer-events: none;
  user-select: none;
}

.iban-masked-input__control {
  z-index: 2;
  min-width: 0;
  outline: 0;
  background: transparent;
  color: inherit;
  caret-color: #233b50;
}

.iban-masked-input__control::placeholder {
  color: #7f8d99;
  opacity: 0.82;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  letter-spacing: normal;
  text-transform: none;
}
</style>
